import React from "react";
import PropTypes from "prop-types";
import { Button, ButtonPurpose, ButtonSize } from "shared/Button";
import Checkbox from "shared/@forms/Checkbox";
import formatDate from "shared/@utils/formatDate";
import styles from "./FileListSection.module.css";

const FILE_TYPE_ICONS = {
  Audio: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
  ),
  Video: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  ),
};

function FileTypeIcon({ type }) {
  const icon = FILE_TYPE_ICONS[type] || FILE_TYPE_ICONS.Audio;
  return (
    <div className={styles.fileTypeIcon} aria-label={type}>
      {icon}
      <span className={styles.fileTypeLabel}>{type}</span>
    </div>
  );
}

FileTypeIcon.propTypes = {
  type: PropTypes.string.isRequired,
};

function FileRow({ file, onArchive, onUnarchive, isArchived }) {
  return (
    <div className={styles.fileRow}>
      <FileTypeIcon type={file.type} />
      <div className={styles.fileMeta}>
        <span className={styles.fileId}>{file.id}</span>
        <span className={styles.fileVersionDate}>
          V{file.version} {file.date ? formatDate(file.date, "M/DD/YYYY") : ""}
        </span>
      </div>
      {isArchived ? (
        <Button
          purpose={ButtonPurpose.save}
          size={ButtonSize.small}
          onClick={() => onUnarchive(file.id)}
        >
          Unarchive
        </Button>
      ) : (
        <Button
          purpose={ButtonPurpose.delete}
          size={ButtonSize.small}
          onClick={() => onArchive(file.id)}
        >
          Archive
        </Button>
      )}
    </div>
  );
}

FileRow.propTypes = {
  file: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    version: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
  }).isRequired,
  onArchive: PropTypes.func,
  onUnarchive: PropTypes.func,
  isArchived: PropTypes.bool,
};

FileRow.defaultProps = {
  onArchive: () => {},
  onUnarchive: () => {},
  isArchived: false,
};

export function FileListSectionV1({
  label,
  showArchiveLabel,
  files,
  archivedFiles,
  onArchive,
  onUnarchive,
  showArchive,
  onToggleShowArchive,
}) {
  return (
    <section className={styles.container}>
      <div className={styles.headerRow}>
        <h3 className={styles.heading}>{label}</h3>
        <div className={styles.showArchiveBox}>
          <Checkbox
            id="file-list-show-archive"
            size={16}
            checked={showArchive}
            onChange={onToggleShowArchive}
          />
          <label
            htmlFor="file-list-show-archive"
            className={styles.showArchiveLabel}
          >
            {showArchiveLabel}
          </label>
        </div>
      </div>

      <div className={styles.headerDivider} />

      {files.length > 0 && (
        <ul className={styles.fileList} aria-label="Active files">
          {files.map((file) => (
            <li key={file.id}>
              <FileRow file={file} onArchive={onArchive} isArchived={false} />
            </li>
          ))}
        </ul>
      )}

      {showArchive && (
        <>
          <h4 className={styles.archiveHeading}>Archived Files</h4>
          <div className={styles.archiveDivider} />
          {archivedFiles.length > 0 ? (
            <ul className={styles.fileList} aria-label="Archived files">
              {archivedFiles.map((file) => (
                <li key={file.id}>
                  <FileRow
                    file={file}
                    onUnarchive={onUnarchive}
                    isArchived={true}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.emptyMessage}>No archived files.</p>
          )}
        </>
      )}
    </section>
  );
}

FileListSectionV1.propTypes = {
  label: PropTypes.string,
  showArchiveLabel: PropTypes.string,
  files: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      version: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
    }),
  ),
  archivedFiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      version: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
    }),
  ),
  onArchive: PropTypes.func,
  onUnarchive: PropTypes.func,
  showArchive: PropTypes.bool,
  onToggleShowArchive: PropTypes.func,
};

FileListSectionV1.defaultProps = {
  label: "Files",
  showArchiveLabel: "Show Archive",
  files: [],
  archivedFiles: [],
  onArchive: () => {},
  onUnarchive: () => {},
  showArchive: false,
  onToggleShowArchive: () => {},
};

export default FileListSectionV1;
